import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import * as AuthActions from '../../store/auth/auth.actions';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service';
import { UrlService } from '../services/url.service';
import { SwalService } from '../services';

// Token de contexte pour ignorer l'authentification
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

// Variable globale pour gérer l'état du refresh token
let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const AuthInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store<AppState>);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const swalService = inject(SwalService);

  // Vérifier si on est dans le navigateur
  if (!isPlatformBrowser(platformId)) {
    // En environnement serveur, ne pas ajouter de token et ne pas gérer les erreurs 401
    return next(request);
  }

  // Vérifier si la requête doit ignorer l'authentification
  if (request.context.get(SKIP_AUTH)) {
    console.log('🔐 Intercepteur: Requête marquée pour ignorer l\'authentification');
    return next(request);
  }

  const token = authService.getToken();

  if (token) {
    const authReq = request.clone({
      headers: request.headers
        .set('Authorization', `Bearer ${token}`)
        .set('token', `Bearer ${token}`),
    });

    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Vérifier à nouveau si on est dans le navigateur avant de traiter l'erreur
        if (!isPlatformBrowser(platformId)) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          console.log(
            '🔐 Intercepteur: Erreur 401 détectée, tentative de refresh token'
          );

          // Si on n'est pas déjà en train de rafraîchir le token
          if (!isRefreshing) {
            isRefreshing = true;
            refreshTokenSubject.next(null);

            const refreshToken = authService.getRefreshToken();

            if (refreshToken) {
              console.log('🔐 Intercepteur: Tentative de refresh token');

              return authService.refreshToken().pipe(
                switchMap((response: any) => {
                  console.log('🔐 Intercepteur: Refresh token réussi');

                  // Sauvegarder les nouveaux tokens
                  if (response.accessToken) {
                    authService.setToken(response.accessToken);
                  }
                  if (response.refreshToken) {
                    authService.setRefreshToken(response.refreshToken);
                  }

                  isRefreshing = false;
                  refreshTokenSubject.next(response.accessToken);

                  // Relancer la requête originale avec le nouveau token
                  const newToken = authService.getToken();
                  const retryReq = request.clone({
                    headers: request.headers
                      .set('Authorization', `Bearer ${newToken}`)
                      .set('token', `Bearer ${newToken}`),
                  });

                  return next(retryReq);
                }),
                catchError((refreshError) => {
                  console.log(
                    '🔐 Intercepteur: Échec du refresh token, déconnexion'
                  );

                  isRefreshing = false;
                  refreshTokenSubject.next(null);

                  // Déconnecter l'utilisateur
                  authService.logout();
                  store.dispatch(AuthActions.logout());

                  // Afficher une notification à l'utilisateur
                  swalService.warning(
                    'Session expirée',
                    'Votre session a expiré. Vous avez été déconnecté.'
                  );

                  // Rediriger vers la page d'accueil
                  const urlService = new UrlService();
                  router.navigate(urlService.buildRouteSegments(''));

                  return EMPTY;
                })
              );
            } else {
              console.log(
                '🔐 Intercepteur: Aucun refresh token disponible, déconnexion'
              );

              isRefreshing = false;
              refreshTokenSubject.next(null);

              // Déconnecter l'utilisateur
              authService.logout();
              store.dispatch(AuthActions.logout());

              // Afficher une notification à l'utilisateur
              swalService.warning(
                'Session expirée',
                'Votre session a expiré. Vous avez été déconnecté.'
              );

              // Rediriger vers la page d'accueil
              const urlService = new UrlService();
              router.navigate(urlService.buildRouteSegments(''));

              return EMPTY;
            }
          } else {
            // Si on est déjà en train de rafraîchir, attendre que le refresh soit terminé
            return refreshTokenSubject.pipe(
              filter((token) => token !== null),
              take(1),
              switchMap(() => {
                const newToken = authService.getToken();
                const retryReq = request.clone({
                  headers: request.headers
                    .set('Authorization', `Bearer ${newToken}`)
                    .set('token', `Bearer ${newToken}`),
                });
                return next(retryReq);
              })
            );
          }
        }

        return throwError(() => error);
      })
    );
  } else {
    console.log(
      '🔐 Intercepteur: Aucun token trouvé, requête sans authentification'
    );
  }

  return next(request);
};
